// src/components/Hermes/RuntimeHint.tsx
//
// 🔒 RuntimeHint V1.0（占位版）
// 冻结日期：2026-07-17
//
// 说明：
// 当前为占位版本，仅保证编译通过。
// Phase 7 将升级为 Runtime Registry 驱动版本。
//
// 升级计划：
// - 从 RuntimeManager 获取 RuntimeInfo
// - 支持 RuntimeStatus 状态显示
// - 显示 intent + view 完整链路

import { RuntimeName } from '../../types/runtime';
import { HermesIntent } from '../../types/hermes.types';
import { WorkspaceView } from '../../types/workspace';

interface RuntimeHintProps {
  runtime?: RuntimeName | null;
  intent?: HermesIntent | null;
  view?: WorkspaceView | null;
  loading?: boolean;
  detected?: boolean;
}

// 临时映射（Phase 7 将改为从 Runtime Registry 获取）
const runtimeIcons: Partial<Record<RuntimeName, string>> = {
  [RuntimeName.Hermes]: '🧠',
  [RuntimeName.Quant]: '📈',
  [RuntimeName.Knowledge]: '📚',
  [RuntimeName.Business]: '🏢',
};

const runtimeLabels: Partial<Record<RuntimeName, string>> = {
  [RuntimeName.Hermes]: 'AI 大脑',
  [RuntimeName.Quant]: '量化系统',
  [RuntimeName.Knowledge]: '知识库',
  [RuntimeName.Business]: '业务系统',
};

const viewLabels: Partial<Record<WorkspaceView, string>> = {
  [WorkspaceView.Cockpit]: '驾驶舱',
  [WorkspaceView.Trading]: '交易视图',
  [WorkspaceView.Knowledge]: '知识视图',
  [WorkspaceView.Agent]: 'Agent 视图',
  [WorkspaceView.Workflow]: '工作流',
};

const intentLabels: Partial<Record<HermesIntent, string>> = {
  [HermesIntent.Chat]: '对话',
  [HermesIntent.Trading]: '交易分析',
  [HermesIntent.Knowledge]: '知识检索',
  [HermesIntent.Agent]: 'Agent 调度',
  [HermesIntent.Business]: '业务分析',
  [HermesIntent.Summarize]: '总结',
  [HermesIntent.Translate]: '翻译',
  [HermesIntent.Plan]: '规划',
  [HermesIntent.Analyze]: '分析',
  [HermesIntent.Unknown]: '未知意图',
};

export default function RuntimeHint({
  runtime = null,
  intent = null,
  view = null,
  loading = false,
  detected = false,
}: RuntimeHintProps) {
  // 加载状态
  if (loading) {
    return (
      <div className="mb-2 text-xs text-gray-500 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>
        <span>正在识别意图...</span>
      </div>
    );
  }

  // 无数据时不显示
  if (!runtime && !intent && !view) {
    return null;
  }

  const icon = runtime ? runtimeIcons[runtime] || '⚙️' : '🧠';
  const label = runtime ? runtimeLabels[runtime] || runtime : 'Hermes';
  const intentLabel = intent ? intentLabels[intent] || intent : null;
  const viewLabel = view ? viewLabels[view] || view : null;

  return (
    <div className="mb-2 text-xs text-gray-400 flex items-center gap-1.5 flex-wrap">
      <span className="text-green-400">●</span>
      <span className="flex items-center gap-1">
        <span>{icon}</span>
        <span className="text-white/70">{label}</span>
      </span>

      {intentLabel && (
        <>
          <span className="text-gray-500">·</span>
          <span className="text-gray-400">意图: {intentLabel}</span>
        </>
      )}

      {viewLabel && (
        <>
          <span className="text-gray-500">→</span>
          <span className="text-blue-400">打开 {viewLabel}</span>
        </>
      )}

      {detected && (
        <span className="text-emerald-400/60 text-[10px]">✓ 已识别</span>
      )}
    </div>
  );
}